import { useTranslation } from 'react-i18next'
import { Textarea } from '@/components/v2/ui'
import { AIFieldButton } from '@/components/v2/products/ai-field'
import type { ContentFieldKey } from './channel-constants'
import { fieldTypeForChannelField } from './channelFieldType'

interface ChannelEditorFieldProps {
  label: ContentFieldKey
  value: string
  masterValue: string
  isInherited: boolean
  isMasterEditor: boolean
  multiline?: boolean
  rows?: number
  productId: string
  channelId: string
  onChange: (value: string) => void
  onToggleInherit: () => void
}

function LinkIcon({ dashed }: { dashed: boolean }) {
  return (
    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
        d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.1" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
        strokeDasharray={dashed ? '3 3' : undefined}
        d="M10.172 13.828a4 4 0 015.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
    </svg>
  )
}

export function ChannelEditorField({
  label, value, masterValue, isInherited, isMasterEditor,
  multiline = false, rows = 2, productId, channelId, onChange, onToggleInherit,
}: ChannelEditorFieldProps) {
  const { t } = useTranslation()
  const isReadOnly = isInherited && !isMasterEditor
  const ft = fieldTypeForChannelField(label)
  const fieldCls = isReadOnly
    ? 'w-full bg-neutral-100 dark:bg-neutral-900/50 border border-neutral-200 dark:border-neutral-800 rounded-md px-3 py-2 text-sm text-neutral-500 italic cursor-default resize-none'
    : 'w-full bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-primary-800/40 rounded-md px-3 py-2 text-sm text-neutral-900 dark:text-neutral-100 resize-y focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20'
  const masterFieldCls = 'w-full bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-700 rounded-md px-3 py-2 text-sm text-neutral-900 dark:text-neutral-100 resize-y focus:outline-none focus:border-primary-500'

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-2xs font-medium text-neutral-500 uppercase tracking-wider">
          {t(label)}
        </label>
        {!isMasterEditor && (
          <button onClick={onToggleInherit}
            className="flex items-center gap-1 px-1.5 py-0.5 rounded text-2xs font-medium transition-colors hover:bg-primary-500/10">
            <LinkIcon dashed={!isInherited} />
            <span className={isInherited ? 'text-neutral-400' : 'text-primary-400'}>
              {isInherited ? t('productContent.channels.inheritedFromMaster') : t('productContent.channels.overriddenLabel')}
            </span>
          </button>
        )}
        {ft && !isReadOnly && (
          <AIFieldButton productId={productId} fieldType={ft} channelId={channelId} />
        )}
      </div>
      {multiline ? (
        <Textarea rows={rows} readOnly={isReadOnly}
          value={value} onChange={(e) => onChange(e.target.value)}
          className={isMasterEditor ? masterFieldCls : fieldCls} />
      ) : (
        <input type="text" readOnly={isReadOnly}
          value={value} onChange={(e) => onChange(e.target.value)}
          className={isMasterEditor ? masterFieldCls.replace('resize-y ', '') : fieldCls.replace('resize-none ', '')} />
      )}
      {!isInherited && !isMasterEditor && masterValue && (
        <div className="mt-1 text-2xs text-neutral-600 truncate">
          Master: &quot;{masterValue.slice(0, 60)}{masterValue.length > 60 ? '…' : ''}&quot;
        </div>
      )}
    </div>
  )
}
